import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  NETWORK_ROUTES,
  ORDER_ROUTES,
  STORE_ROUTES,
  downlineResponseSchema,
  networkNodeResponseSchema,
  orderListResponseSchema,
  storeResponseSchema,
  type MyOrdersQuery,
  type StoreInput,
  type StoreWithItems,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { isApiError } from "@/shared/api/client";

/**
 * El panel del franquiciatario habla con cuatro esquinas del API: su
 * TIENDA (store), sus PEDIDOS y VENTAS (orders/mine), y su RED
 * (network). Todo con sesión; el "eres franquiciatario" lo exige el
 * servidor donde corresponde.
 */
const NO_PREFIX = { baseUrl: "" } as const;

// ─── Mi tienda ───────────────────────────────────────────────────────

export const miNegocioKeys = {
  store: ["mi-negocio", "store"] as const,
  orders: (query: MyOrdersQuery) => ["mi-negocio", "orders", query] as const,
  network: ["mi-negocio", "network"] as const,
  downline: ["mi-negocio", "downline"] as const,
};

export function useMyStore() {
  return useQuery({
    queryKey: miNegocioKeys.store,
    queryFn: () => authFetch(STORE_ROUTES.mine, storeResponseSchema, NO_PREFIX),
  });
}

export function useUpdateMyStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StoreInput): Promise<StoreWithItems> =>
      authFetch(STORE_ROUTES.mine, storeResponseSchema, {
        ...NO_PREFIX,
        method: "PUT",
        body: input,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(miNegocioKeys.store, data);
    },
  });
}

// ─── Mis pedidos y mis ventas ────────────────────────────────────────

export function useMyOrders(query: MyOrdersQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    perPage: String(query.perPage),
    role: query.role,
  });
  return useQuery({
    queryKey: miNegocioKeys.orders(query),
    queryFn: () =>
      authFetch(`${ORDER_ROUTES.mine}?${params.toString()}`, orderListResponseSchema, NO_PREFIX),
    placeholderData: (previous) => previous,
  });
}

// ─── Mi red ──────────────────────────────────────────────────────────

/**
 * El nodo propio, o `null` si la cuenta todavía no está en la red — el
 * 404 aquí no es un error: es "aún no tienes código", y la pantalla
 * ofrece activarlo.
 */
export function useMyNetworkNode() {
  return useQuery({
    queryKey: miNegocioKeys.network,
    queryFn: async () => {
      try {
        const data = await authFetch(NETWORK_ROUTES.me, networkNodeResponseSchema, NO_PREFIX);
        return data.node;
      } catch (error) {
        if (isApiError(error) && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useEnrollInNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await authFetch(NETWORK_ROUTES.enroll, networkNodeResponseSchema, {
        ...NO_PREFIX,
        method: "POST",
        // Un franquiciatario entra a la red como DISTRIBUIDOR: es quien
        // revende. Sin `referralCode`: quien activa desde su panel entra
        // como raíz de su propia línea si nadie lo invitó.
        body: { customerType: "distributor" },
      });
      return data.node;
    },
    /*
     * ── Se ESCRIBE el nodo en la caché, no se invalida y a esperar ────
     * Invalidar dejaba la pantalla diciendo "tu cuenta todavía no está
     * en la red" con el nodo YA creado en el servidor (Anahí, 28-ago:
     * "no me deja activar mi código" — los logs mostraban el POST
     * respondiendo 201 y ningún GET después). El POST ya devuelve el
     * nodo: se guarda y la pantalla cambia en el mismo instante, sin
     * depender de que un refetch ocurra.
     */
    onSuccess: (node) => {
      queryClient.setQueryData(miNegocioKeys.network, node);
      void queryClient.invalidateQueries({ queryKey: miNegocioKeys.downline });
    },
  });
}

export function useMyDownline() {
  return useQuery({
    queryKey: miNegocioKeys.downline,
    queryFn: () =>
      // 200 (el tope del contrato): el árbol de la pantalla de red se
      // arma con la línea completa, no con una página.
      authFetch(`${NETWORK_ROUTES.downline}?page=1&perPage=200`, downlineResponseSchema, NO_PREFIX),
    /*
     * ── Esta pantalla SÍ se refresca sola ─────────────────────────────
     * «Dan invitó a Sofi y no aparece en su red» (Anahí, 1-sep). En la
     * base estaba todo bien y el API la devolvía: lo que no ocurría era
     * el refetch. Con los valores por omisión del sistema
     * (`refetchOnWindowFocus: false` en `providers.tsx`, `staleTime` de
     * 30 s), quien deja "Mi red" abierta, manda su enlace por WhatsApp y
     * vuelve a la pestaña, ve la MISMA lista vacía de antes — y concluye,
     * con razón, que el sistema no registró a quien invitó.
     *
     * Es la única pantalla del sistema cuyo propósito entero es esperar
     * a que ALGUIEN MÁS haga algo. El valor global es correcto para un
     * catálogo o un formulario; aquí es justo lo contrario de lo que
     * hace falta, así que se invierte solo aquí:
     *
     * · `refetchOnWindowFocus` — volver a la pestaña ES la pregunta
     *   "¿ya llegó?". Se contesta pidiendo, no enseñando lo viejo.
     * · `refetchInterval` — para quien se queda mirando la pantalla sin
     *   cambiar de ventana. Un minuto: la red de nadie cambia tan rápido
     *   como para justificar más, y es una consulta barata.
     */
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}
